import React from 'react';
import type { FaqIfThen } from '../../data/types';
import { Card } from '../../components/Components';

interface IfThenBlockProps {
  items: FaqIfThen[];
}

// Shared chip styling for IF/THEN labels. Mono uppercase reads as
// a category label; the background fill turns the labels into actual
// chips so they stand out from the body text and from each other.
// Fixed width + centered text so "IF" (2 chars) and "THEN" (4 chars)
// produce identical chip widths — keeps the body text on both rows
// starting at the same horizontal position.
const chipBase: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  padding: '3px 0',
  borderRadius: 4,
  flexShrink: 0,
  display: 'inline-block',
  textAlign: 'center',
  width: 52,
  // Slight optical alignment with the first text baseline beside it
  lineHeight: 1.4,
};

export const IfThenBlock: React.FC<IfThenBlockProps> = ({ items }) => {
  if (!items || items.length === 0) return null;

  return (
    <div
      style={{
        marginTop: 'var(--space-4)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-2)',
      }}
    >
      {items.map((item, idx) => (
        <Card
          key={idx}
          padding="sm"
          style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
        >
          {/* IF row */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <span
              style={{
                ...chipBase,
                background: 'var(--color-bg-tertiary)',
                color: 'var(--color-text-primary)',
              }}
            >
              If
            </span>
            <span
              style={{
                fontSize: 13.5,
                lineHeight: 1.45,
                color: 'var(--color-text-primary)',
                fontWeight: 600,
              }}
            >
              {item.condition}
            </span>
          </div>
          {/* THEN row — accent-tinted chip + emphasized action text so
              the user's eye lands on the actionable instruction first */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <span
              style={{
                ...chipBase,
                background: 'var(--color-accent-muted)',
                color: 'var(--color-accent)',
              }}
            >
              Then
            </span>
            <span
              style={{
                fontSize: 13.5,
                lineHeight: 1.45,
                color: 'var(--color-text-primary)',
                fontWeight: 500,
              }}
            >
              {item.action}
            </span>
          </div>
        </Card>
      ))}
    </div>
  );
};
